import {
  LEG_GROUP,
  STICK_GROUP,
  vertWellCircleRadius
} from "constants/mapLayers.constants";
import { dequal } from "dequal";
import mapboxgl from "mapbox-gl";

// Base-layer wells are background context: flat gray rather than the interactive
// well layer's data-driven colour. Midpoint of the n/a (#AAAAAA) and Others
// (#777B87) bin greys (EVA-201).
// Must match eva-map-svc WellRasterRenderer.BaseLayerPointColour.
export const BASE_WELL_GRAY = "#919399";

// EVA-35: the base layer used to render a well-status glyph per point (QGIS-style
// icons ported from the production GeoServer SLD, keyed off the tile's
// `well_statu` attribute). Glyph placement and vocabulary are under
// investigation, so they're removed for now in favor of plain, single-colour
// circles — no sprite/image loading, no collision placement. The `well_statu`
// attribute still flows in the tile; only the styling changed.

// EVA-201: licences (Azure) and permits (AWS) draw dashed and with a hollow dot;
// every other status, locations included, stays solid and filled. Exact-match
// against the tile's `well_statu` — both vocabularies live in one list.
export const LICENCE_PERMIT_STATUSES = [
  "01. LIC",
  "10. IDLE LIC",
  "Permit Submitted",
  "Permit Approved",
  "Permit Cancelled"
];

// A missing `well_statu` falls through `match` to false, so a tile without it
// draws as a non-permit.
export const IS_LICENCE_OR_PERMIT: mapboxgl.Expression = [
  "match",
  ["get", "well_statu"],
  LICENCE_PERMIT_STATUSES,
  true,
  false
];

// Mapbox drops zero-length dash ranges (LineAtlas.addRegularDash), so [1, 0]
// is solid; a single-value array like [2] is not.
const SOLID_DASH = ["literal", [1, 0]];

// line-dasharray is in line-width units, so dividing by width keeps the dash a
// fixed pixel length whatever the thickness.
const DASH_PX = 8;
const GAP_PX = 6;
const PERIOD_PX = DASH_PX + GAP_PX;
const MAX_REPEATS = 20;

// Mapbox truncates a data-driven dasharray's total length to an integer, so
// the pattern repeats until its length is whole, then sits just above it.
const permitDashPattern = (width: number): number[] => {
  let best = { k: 1, n: 1, err: Infinity };
  for (let k = 1; k <= MAX_REPEATS; k++) {
    const n = Math.max(1, Math.round((PERIOD_PX * k) / width));
    const err = Math.abs((n * width) / k - PERIOD_PX);
    if (err < best.err) best = { k, n, err };
    if (err < 1e-6) break;
  }
  const { k, n } = best;
  const scale = (n + 1e-3) / ((PERIOD_PX * k) / width);
  return Array.from({ length: k }, () => [
    (DASH_PX / width) * scale,
    (GAP_PX / width) * scale
  ]).flat();
};

// Legs and sticks are forced solid so a permit well's legs never dash.
export const wellLineDasharray = (width: number): mapboxgl.Expression => [
  "match",
  ["get", "group"],
  [LEG_GROUP, STICK_GROUP],
  SOLID_DASH,
  ["case", IS_LICENCE_OR_PERMIT, ["literal", permitDashPattern(width)], SOLID_DASH]
];

const RELAYOUT_FALLBACK_MS = 1000;

interface WidthSync {
  inFlight: boolean;
  pending?: number;
  cancel?: () => void;
}

// Keyed per map: SingleWellMap and settings preview maps share layer ids.
const widthSyncs = new WeakMap<mapboxgl.Map, Map<string, WidthSync>>();

const widthSyncsFor = (mapbox: mapboxgl.Map) => {
  const existing = widthSyncs.get(mapbox);
  if (existing) return existing;
  const created = new Map<string, WidthSync>();
  widthSyncs.set(mapbox, created);
  const clearOnRemove = () => {
    created.forEach((sync) => sync.cancel?.());
    created.clear();
  };
  // eslint-disable-next-line listeners/no-missing-remove-event-listener -- Mapbox GL handles its own listener lifecycle; "remove" fires once as the map is destroyed.
  mapbox.once("remove", clearOnRemove);
  return created;
};

const sourceLoaded = (mapbox: mapboxgl.Map, sourceId: string): boolean => {
  try {
    return mapbox.isSourceLoaded(sourceId);
  } catch {
    return true;
  }
};

// Mapbox applies the paint change and starts the tile reload inside the next
// render, so source readiness is only meaningful after that render.
const afterRelayout = (
  mapbox: mapboxgl.Map,
  sourceId: string,
  done: () => void
): (() => void) => {
  let finished = false;
  const stop = () => {
    finished = true;
    clearTimeout(timeout);
    try {
      mapbox.off("render", onRender);
      mapbox.off("sourcedata", onSourceData);
    } catch {
      // Map torn down while waiting.
    }
  };
  const finish = () => {
    if (finished) return;
    stop();
    done();
  };
  const onSourceData = (e: { sourceId?: string }) => {
    if (e.sourceId === sourceId && sourceLoaded(mapbox, sourceId)) finish();
  };
  const onRender = () => {
    mapbox.off("render", onRender);
    if (sourceLoaded(mapbox, sourceId)) finish();
    else mapbox.on("sourcedata", onSourceData);
  };
  const timeout = setTimeout(finish, RELAYOUT_FALLBACK_MS);
  mapbox.on("render", onRender);
  return () => {
    if (!finished) stop();
  };
};

// Dash length in pixels is dash x width, so the width must not change until the
// new dasharray's tile buckets exist or the dashes stretch before snapping back.
export const setWellLineWidth = (
  mapbox: mapboxgl.Map,
  layerId: string,
  width: number
): void => {
  const layer = mapbox.getLayer(layerId);
  if (!layer) return;
  const syncs = widthSyncsFor(mapbox);
  let sync = syncs.get(layerId);
  if (!sync) {
    sync = { inFlight: false };
    syncs.set(layerId, sync);
  }
  if (sync.inFlight) {
    sync.pending = width;
    return;
  }

  const dash = wellLineDasharray(width);
  if (dequal(mapbox.getPaintProperty(layerId, "line-dasharray"), dash)) {
    mapbox.setPaintProperty(layerId, "line-width", width);
    return;
  }

  mapbox.setPaintProperty(layerId, "line-dasharray", dash);
  const current = sync;
  current.inFlight = true;
  current.cancel = afterRelayout(mapbox, (layer as { source: string }).source, () => {
    current.cancel = undefined;
    current.inFlight = false;
    try {
      if (mapbox.getLayer(layerId)) mapbox.setPaintProperty(layerId, "line-width", width);
    } catch {
      // Map torn down between schedule and fire.
    }
    const next = current.pending;
    current.pending = undefined;
    if (next !== undefined && next !== width) setWellLineWidth(mapbox, layerId, next);
  });
};

// Hollow = transparent fill + coloured stroke. Opacity can't carry this: the
// hover dimmer owns `circle-opacity` and `circle-stroke-opacity` at runtime.
// Both colours must be written together wherever a dot colour is set.
export const hollowDotColorPaint = (
  color: mapboxgl.Expression | string
): mapboxgl.CirclePaint => ({
  "circle-color": ["case", IS_LICENCE_OR_PERMIT, "rgba(0, 0, 0, 0)", color],
  "circle-stroke-color": color
});

export function setHollowDotColors(
  mapbox: mapboxgl.Map,
  layerId: string,
  color: mapboxgl.Expression | string
) {
  for (const [prop, value] of Object.entries(hollowDotColorPaint(color))) {
    mapbox.setPaintProperty(layerId, prop, value);
  }
}

export const HOLLOW_DOT_STROKE_PAINT: mapboxgl.CirclePaint = {
  "circle-stroke-opacity": 1
};

// Mapbox draws the stroke fully outside circle-radius, so radius + stroke is the
// outer size. A fixed-px ring keeps hollow and solid dots (solid ones stroke in
// their fill colour) at one diameter without thickening the ring as dots grow.
export const WELL_DOT_STROKE_PX = 2;
// 0.5px under the Well ring so base dots hide beneath Well dots when stacked.
export const BASE_DOT_STROKE_PX = 1.5;

// Takes and returns a pure-zoom interpolate; a data-driven radius would relayout tiles.
export const insetDotRadius = (
  outerRamp: mapboxgl.Expression,
  insetPx: number
): mapboxgl.Expression => {
  const [interpolate, curve, input, ...stops] = outerRamp as unknown[];
  return [
    interpolate,
    curve,
    input,
    ...stops.map((value, i) =>
      i % 2 === 1 ? Math.max(0, (value as number) - insetPx) : value
    )
  ] as mapboxgl.Expression;
};

// Overlays that must cover a dot use the full vertWellCircleRadius.
export const wellDotSizePaint = (dotThickness?: number) => ({
  "circle-radius": insetDotRadius(
    vertWellCircleRadius(dotThickness) as mapboxgl.Expression,
    WELL_DOT_STROKE_PX
  ),
  "circle-stroke-width": WELL_DOT_STROKE_PX
});
